/**
 * Compare two version strings
 * Returns: -1 if v1 < v2, 0 if equal, 1 if v1 > v2
 */
export function compareVersions(v1: string, v2: string): number {
  const clean1 = v1.replace(/^v/, '')
  const clean2 = v2.replace(/^v/, '')

  const parts1 = clean1.split('.').map(Number)
  const parts2 = clean2.split('.').map(Number)

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0
    const part2 = parts2[i] || 0

    if (part1 < part2) return -1
    if (part1 > part2) return 1
  }

  return 0
}

/**
 * Validate version string format
 */
export function isValidVersion(version: string): boolean {
  if (version === 'latest') return true
  return /^v?\d+\.\d+\.\d+$/.test(version)
}

/**
 * Resolve a requested version string to the best matching available version.
 *
 * Resolution rules:
 *  1. Exact match (including "latest") → return it as-is.
 *  2. Otherwise find the highest semver version that is ≤ requested.
 *     Example: requested=1.2.4, available=[1.2.3, 1.3.0] → 1.2.3
 *  3. If the requested version is older than every available version,
 *     fall back to the oldest available semver version.
 *  4. Returns undefined when the requested string is not a valid version.
 */
export function resolveVersion<T extends { version: string }>(
  requested: string,
  availableVersions: T[]
): T | undefined {
  // Rule 1: exact match (handles "latest" too)
  const exact = availableVersions.find(v => v.version === requested)
  if (exact) return exact

  if (!isValidVersion(requested)) return undefined

  // Work only with proper semver entries (skip the "latest" alias)
  const semver = availableVersions
    .filter(v => v.version !== 'latest' && isValidVersion(v.version))
    .sort((a, b) => compareVersions(b.version, a.version)) // descending

  // Rule 2: highest available version that does not exceed the requested one
  const match = semver.find(v => compareVersions(v.version, requested) <= 0)
  if (match) return match

  // Rule 3: requested is older than everything we have — use the oldest docs
  return semver[semver.length - 1]
}
